import { useState } from 'react';

import { EMAIL_REGEX } from '@/constants/static';
import { Input, Modal } from '@/design-system';

export type AddDirectorModalProps = {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  existingEmails: string[];
  isSending: boolean;
  serverError?: string;
  onSubmit: (_email: string) => void;
  /** Clears a failed invitation so editing the email re-enables the primary action. */
  onEmailChange?: () => void;
};

const DESCRIPTION =
  'Enter the director’s email address. We’ll send an invitation to join this entity.';
const HINT = 'Invitation will be sent to this address';
const INVALID_EMAIL = 'Enter a valid email address';
const ALREADY_INVITED = 'This person has already been invited';

export const AddDirectorModal = ({
  open,
  onOpenChange,
  existingEmails,
  isSending,
  serverError,
  onSubmit,
  onEmailChange,
}: AddDirectorModalProps) => {
  const [email, setEmail] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  // The dialog stays mounted between openings, so each opening starts a new invitation.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setEmail('');
  }

  const trimmed = email.trim();
  const isValidEmail = EMAIL_REGEX.test(trimmed);
  const isDuplicate = existingEmails.some(
    (existing) => existing.toLowerCase() === trimmed.toLowerCase(),
  );

  const error = !trimmed
    ? serverError
    : !isValidEmail
      ? INVALID_EMAIL
      : isDuplicate
        ? ALREADY_INVITED
        : serverError;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showClose
      maxWidth={504}
      title="Add director"
      description={DESCRIPTION}
      secondaryAction={{
        label: 'Cancel',
        disabled: isSending,
        onClick: () => onOpenChange(false),
      }}
      primaryAction={{
        label: isSending ? 'Sending…' : 'Send invitation',
        disabled: isSending || !isValidEmail || isDuplicate || Boolean(serverError),
        onClick: () => onSubmit(trimmed),
      }}
    >
      <Input
        size="lg"
        required
        label="Email address"
        aria-label="Email address"
        placeholder="alex.morgan@company.com"
        value={email}
        disabled={isSending}
        state={error ? 'destructive' : 'default'}
        helperText={error ?? HINT}
        onChange={(event) => {
          setEmail(event.target.value);
          if (serverError) onEmailChange?.();
        }}
      />
    </Modal>
  );
};
